"use client";

import React, { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="214665" className="flex gap-6">
        <div className="h-4 w-16 bg-muted/50 animate-pulse rounded" />
        <div className="h-4 w-16 bg-muted/50 animate-pulse rounded" />
        <div className="h-4 w-16 bg-muted/50 animate-pulse rounded" />
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative inline-flex h-10 w-max items-center justify-center bg-transparent px-2 py-2 text-sm font-medium text-foreground/80 transition-colors hover:text-foreground focus:text-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50 font-default">
                {item.label}
                <span className="absolute bottom-1 left-0 right-0 h-[2px] scale-x-0 bg-primary transition-transform duration-300 ease-out group-hover:scale-x-100" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav({
  setOpen
}: {
  setOpen: (open: boolean) => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-6 p-4">
        <div className="h-6 w-32 bg-muted/50 animate-pulse rounded" />
        <div className="h-6 w-24 bg-muted/50 animate-pulse rounded" />
        <div className="h-6 w-28 bg-muted/50 animate-pulse rounded" />
      </div>;
  }
  return <div className="flex flex-col space-y-6 mt-8">
      {menuItems.map((item, i) => <div key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-xl font-medium text-foreground/90 hover:text-primary transition-colors block py-2 border-b border-border/30 font-default" onClick={() => setOpen(false)}>
            {item.label}
          </Link>
        </div>)}
      <div className="pt-6">
        <Button variant="outline" className="w-full rounded-lg border-primary/50 bg-transparent text-foreground hover:bg-primary/10 hover:text-primary hover:border-primary font-default text-sm uppercase tracking-widest transition-all duration-300 h-12 hover:ring-2 hover:ring-primary/20" onClick={() => setOpen(false)}>
          Get a Free Audit
        </Button>
      </div>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <section id="header" className={`bg-background text-foreground sticky top-0 z-50 w-full transition-all duration-300 border-b ${isScrolled ? "bg-background/80 backdrop-blur-[12px] border-border/40 shadow-sm" : "border-transparent"}`}>
      <header className="container mx-auto px-4 lg:px-8 h-20 flex items-center justify-between">
        <div className="flex-shrink-0 z-50">
          <Link to="/" className="flex items-center">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634544/img/aerovista_logo.png" alt="Ferio Solutions" className="h-7 w-auto" />
          </Link>
        </div>

        <div className="hidden md:flex flex-1 justify-center">
          <MenuProvider menu_id="42">
            <DesktopNav />
          </MenuProvider>
        </div>

        <div className="hidden md:flex items-center justify-end flex-shrink-0">
          <Button variant="outline" className="rounded-lg border-primary/50 bg-transparent text-foreground hover:bg-primary/10 hover:text-primary hover:border-primary font-default text-xs uppercase tracking-widest transition-all duration-300 px-6 hover:ring-2 hover:ring-primary/20">
            Get a Free Audit
          </Button>
        </div>

        <div className="md:hidden flex items-center">
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-foreground hover:bg-accent/20">
                <Menu className="h-6 w-6" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-background text-foreground border-l border-border/30 w-[300px] sm:w-[400px]">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <div className="flex flex-col h-full pt-12">
                <MenuProvider menu_id="42">
                  <MobileNav setOpen={setIsMobileMenuOpen} />
                </MenuProvider>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>
    </section>;
}